import React from 'react';
import { connect } from 'react-redux';
import PrivateWorkOrderLock from '../private_work_order_lock/PrivateWorkOrderLock';
import WorkOrderStarIcon from '../StarLabels/WorkOrderStarIcon';
import WorkOrderLabelChipsRow from '../StarLabels/WorkOrderLabelChipsRow';
import StarNotePopover from '../StarLabels/StarNotePopover';
import {
	getWorkOrderLabels,
	getWorkOrderStarNote,
	isWorkOrderStarred,
	isWorkOrderLabelsTrackEnabled,
} from '../../data/workOrderStarLabels';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface Props {
	workOrder: { id: number | string; title?: string };
	title?: string;
	onAddLabelClick: () => void;
	// True while the Add Label modal is saving for this WO — shimmers the chips.
	labelsPending?: boolean;
}

const WorkOrderDetailTitleHeader: React.FC<Props> = ({
	workOrder,
	title,
	onAddLabelClick,
	labelsPending = false,
}) => {
	const starred = isWorkOrderStarred(workOrder);
	const starNote = getWorkOrderStarNote(workOrder);
	const workOrderLabels = getWorkOrderLabels(workOrder);
	// Star + label surfaces in the header are hidden when the flag is off.
	const labelsTrackEnabled = isWorkOrderLabelsTrackEnabled();
	const isPrivate = nullSafeGetOrElse('isPrivate', workOrder, false);
	const hasNote = starred && !!starNote;
	const titleText = title != null ? title : workOrder.title;
	// Starred-with-note: the star+note pill takes its own top line; title and labels flow inline below.
	const starOnTop = hasNote;

	const starPill = labelsTrackEnabled ? (
		<StarNotePopover
			workOrderId={workOrder.id}
			initialStarred={starred}
			initialNote={starNote || ''}
		>
			<WorkOrderStarIcon size="md" withDropdown onDark />
		</StarNotePopover>
	) : null;

	const privateLock = (
		<PrivateWorkOrderLock
			workOrder={workOrder}
			style={{ marginTop: -4, marginRight: 8, marginLeft: 8 }}
			fontSize={24}
		/>
	);

	const labelChips = labelsTrackEnabled ? (
		<WorkOrderLabelChipsRow
			labels={workOrderLabels}
			maxVisible={workOrderLabels.length}
			showAddButton
			addButtonVariant="hollowOnDark"
			onAddClick={onAddLabelClick}
			loading={labelsPending}
			layout="inline"
		/>
	) : null;

	const lock = isPrivate ? (
		<span className="ml-2 inline-flex items-center align-middle">{privateLock}</span>
	) : null;

	const labelsRow = (
		<span
			className="ml-3 inline-flex flex-wrap items-center gap-3"
			style={{ transform: 'translateY(-4px)' }}
		>
			{labelChips}
		</span>
	);

	const titleAndLabels = (
		<div className="flex w-full min-w-0 flex-col gap-3">
			<div className="h2regular break-words text-white/90">
				{!starOnTop ? (
					<span className="mr-3 inline-flex items-center align-middle">{starPill}</span>
				) : null}
				{titleText}
				{lock}
				<div className="inline-flex flex-wrap items-center gap-3">{labelsRow}</div>
			</div>
		</div>
	);

	const titleMeasure = (
		<span
			aria-hidden="true"
			className="pointer-events-none invisible absolute left-0 top-0 whitespace-nowrap"
		>
			{titleText}
		</span>
	);

	if (starOnTop) {
		return (
			<div className="work-order-detail-title-header h2regular relative flex w-full min-w-0 flex-col gap-3">
				{titleMeasure}
				<div className="inline-flex items-center">{starPill}</div>
				{titleAndLabels}
			</div>
		);
	}

	return (
		<div className="work-order-detail-title-header relative w-full min-w-0">
			{titleMeasure}
			{titleAndLabels}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	labelsPending: nullSafeGetOrElse(
		`work_order_star_labels.labelsPendingById.${nullSafeGet('workOrder.id', ownProps)}`,
		state,
		false
	),
});

export default connect(mapStateToProps)(WorkOrderDetailTitleHeader);
